import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'

import type { ControllerConfigurationFileContextType } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import { makeConfigFileContextValue } from '@/features/controller/configuration/contexts/ControllerConfigurationFileContext'
import {
  useInvalidateLatestControllerConfiguration,
  useLatestControllerConfiguration,
} from '@/features/controller/configuration/hooks/useLatestControllerConfiguration'
import { useUpdateControllerConfigLatestVersionMutation } from '@/features/controller/configuration/hooks/useUpdateControllerConfigLatestVersionMutation'
import { ControllerConfigTypeId } from '@/features/controller/configuration/types'

interface ControllerConfigurationFileSetContextType {
  appJson: ControllerConfigurationFileContextType
  raspiProperties: ControllerConfigurationFileContextType
}

const ControllerConfigurationFileSetContext = createContext<ControllerConfigurationFileSetContextType | null>(null)

interface ControllerConfigurationFileSetProviderProps {
  children: ReactNode
  controllerId: string
}

export function ControllerConfigurationFileSetProvider({
  children,
  controllerId,
}: Readonly<ControllerConfigurationFileSetProviderProps>) {
  const latestAppJson = useLatestControllerConfiguration({ controllerId, configTypeId: ControllerConfigTypeId.AppJson })
  const latestRaspiProperties = useLatestControllerConfiguration({
    controllerId,
    configTypeId: ControllerConfigTypeId.RaspiProperties,
  })

  const appJsonMutation = useUpdateControllerConfigLatestVersionMutation(controllerId, ControllerConfigTypeId.AppJson)
  const raspiPropertiesMutation = useUpdateControllerConfigLatestVersionMutation(
    controllerId,
    ControllerConfigTypeId.RaspiProperties,
  )

  const invalidateLatestConfig = useInvalidateLatestControllerConfiguration()

  const value = {
    appJson: makeConfigFileContextValue(
      controllerId,
      ControllerConfigTypeId.AppJson,
      latestAppJson,
      appJsonMutation,
      invalidateLatestConfig,
    ),
    raspiProperties: makeConfigFileContextValue(
      controllerId,
      ControllerConfigTypeId.RaspiProperties,
      latestRaspiProperties,
      raspiPropertiesMutation,
      invalidateLatestConfig,
    ),
  }

  return (
    <ControllerConfigurationFileSetContext.Provider value={value}>
      {children}
    </ControllerConfigurationFileSetContext.Provider>
  )
}

export function useControllerConfigurationFileSetContext() {
  const context = useContext(ControllerConfigurationFileSetContext)
  if (!context) {
    throw new Error(
      'useControllerConfigurationFileSetContext must be used within ControllerConfigurationFileSetProvider',
    )
  }
  return context
}
